import { NLMailboxProfile, NLMailboxProfileType, NLSenderProspect } from "@models/Models";
import OauthSessionManager from "@utils/managers/oauth/OauthSessionManager";
import * as networkManager from "@utils/managers/networking/NetworkManager";
import { MailboxError } from "./MailboxError";
import moment from "moment";
import * as Date from "@utils/Date+GetDateFor";
import { kLocalStorageKeys } from "@utils/constants/kLocalStorageKeys";
import { kAppConfig } from "@utils/configurations/AppConfig";

export class GetSenderProspectsService {
	private mailboxProfile: NLMailboxProfile;
	private dateQuery: string | null;
	private lastUpdated: string | null;

    constructor(mailboxProfile: NLMailboxProfile, lookbackDays: [number, number] | null = null, addSavedLastUpdatedToQuery: boolean, forceLastUpdatedAtDate: string | null = null) {
        this.mailboxProfile = mailboxProfile;
        this.dateQuery = GetSenderProspectsService.generateDateQuery(mailboxProfile.id, lookbackDays);
        this.lastUpdated = forceLastUpdatedAtDate || (addSavedLastUpdatedToQuery ? GetSenderProspectsService.getLastUpdatedDate(mailboxProfile.id) : null);
    }

    static generateDateQuery(mailboxProfileId: string, lookbackDays: [number, number] | null): string | null {
        let dateString: string | null = null;

        if (lookbackDays) {
            const afterDateString = Date.getDateFor(lookbackDays[0]);
            const beforeDateString = Date.getDateFor(lookbackDays[1]);

            if (afterDateString && beforeDateString) {
                dateString = `after:${afterDateString} before:${beforeDateString}`;
            }
        } else {
            const backendLastUpdatedAt = this.getLastUpdatedDate(mailboxProfileId);

            if (backendLastUpdatedAt) {
                const dateQuery = this.convertBackendDateToDateQuery(backendLastUpdatedAt);
                if (dateQuery) {
                    dateString = `after:${dateQuery}`;
                }
            } else {
                const initialDateString = Date.getDateFor(kAppConfig.initialProspectQueryFromDay);

                if (initialDateString) {
                    dateString = `after:${initialDateString}`;
                }
            }
        }

        return dateString;
    }

    static getLastUpdatedDate(mailboxProfileId: string): string | null {
        try {
            const lastUpdatedAt = localStorage.getItem(kLocalStorageKeys.Backend.userStatesLastUpdatedAt);

            if (lastUpdatedAt) {
                const lastUpdatedAtDict = JSON.parse(lastUpdatedAt) as Record<string, string>;

                if (lastUpdatedAtDict && Object.prototype.hasOwnProperty.call(lastUpdatedAtDict, mailboxProfileId)) {
                    return lastUpdatedAtDict[mailboxProfileId];
                }
            }

            return null;
        } catch (error) {
            return null;
        }
    }

    static convertBackendDateToDateQuery(backendDate: string): string | null {
        const inputDateFormatter = "YYYY-MM-DD-HH-mm-ss";
        const outputDateFormatter = "YYYY/MM/DD";

        const inputDate = moment(backendDate, inputDateFormatter, true);

        if (!inputDate.isValid()) {
            return null;
        }

        const outputDateString = inputDate.utc().format(outputDateFormatter);
        return outputDateString;
    }

	getSenderProspects(completion: (prospects: NLSenderProspect[] | undefined, error: Error | undefined) => void) {
		const oauthSessionManager = OauthSessionManager.sharedInstance();
		let request: GetSenderProspectsService.RequestModel | undefined = undefined;

		const promise = new Promise<void>((innerResolve, innerReject) => {
			if (this.mailboxProfile.type !== NLMailboxProfileType.Internal) {
				oauthSessionManager
					.fetchAccessTokenForMailboxProfile(this.mailboxProfile.id, this.mailboxProfile.type)
					.then((accessToken) => {
						request = new GetSenderProspectsService.RequestModel(this.mailboxProfile.id, accessToken, this.dateQuery, this.lastUpdated);
						innerResolve();
					})
					.catch((error) => {
						innerReject(error);
						return;
					});
			} else {
				request = new GetSenderProspectsService.RequestModel(this.mailboxProfile.id, null, this.dateQuery, this.lastUpdated);
				innerResolve();
			}
		});

		Promise.all([promise])
			.then(() => {
				if (!request) {
					completion(undefined, undefined);
					return;
				}
				this.makeRequests(request, completion);
			})
			.catch((error) => {
				completion(undefined, error);
			});
	}

	private makeRequests(request: GetSenderProspectsService.RequestModel, completion: (prospects: NLSenderProspect[] | undefined, error: Error | undefined) => void) {
		networkManager
			.getSenderProspects(request)
			.then((jsonData) => {
				if (jsonData.error) {
					const mailboxError = MailboxError.returnError(jsonData.error, jsonData.mailbox_profile_id);
					completion(undefined, mailboxError);
					return;
				}
				completion(jsonData.prospects, undefined);
			})
			.catch((error) => {
				completion(undefined, error);
			});
	}
}

export namespace GetSenderProspectsService {
	export class RequestModel {
		access_token: string | null;
		mailbox_profile_id!: string;
		date_query: string | null;
		last_updated: string | null;

		constructor(mailboxProfileId: string, accessToken: string | null, dateQuery: string | null, lastUpdated: string | null) {
			this.mailbox_profile_id = mailboxProfileId;
			this.access_token = accessToken;
			this.date_query = dateQuery;
			this.last_updated = lastUpdated;
		}
	}
}
